import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import LanguageLink from "../routing/LanguageLink";
import ScrollAnimation from "../../hooks/ScrollAnimation";

const ServicesSection = () => {
  const { t } = useTranslation();

  const icons = ["📝", "⚖️", "💳", "💬", "📄", "📊", "🛟", "👨‍⚖️", "💻"];
  const serviceItems = t("services_section.items", { returnObjects: true }) || [];

  const services = serviceItems.map((item, index) => ({
    id: index + 1,
    icon: icons[index] || "📋",
    ...item
  }));

  const data = {
    title: t("services_section.title"),
    subtitle: t("services_section.subtitle"),
    services: services
  };

  return (
    <section className="bg-white py-12 lg:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <motion.span
            className="inline-block px-4 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-3"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            {t("services_section.badge")}
          </motion.span>

          <motion.h2
            className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            {data.title}
          </motion.h2>

          <motion.p
            className="text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            {data.subtitle}
          </motion.p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.services.map((service, index) => (
            <ScrollAnimation key={service.id} direction="up" delay={index * 0.05}>
              <LanguageLink to="/services" className="block h-full">
                <motion.div
                  className="group bg-white rounded-lg p-5 border-2 border-gray-200 hover:border-blue-600 hover:shadow-lg transition-all duration-300 h-full"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05, duration: 0.4 }}
                  whileHover={{ y: -5 }}
                >
                  {/* Icon & Title Row */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="bg-blue-600 rounded-lg w-10 h-10 flex items-center justify-center shrink-0">
                      <span className="text-xl">{service.icon}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {service.title}
                      </h3>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-xs text-gray-600">
                        <svg className="w-4 h-4 text-blue-600 mr-2 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Arrow indicator */}
                  <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-600 group-hover:text-blue-700">
                      {t("services_section.learn_more")}
                    </span>
                    <svg
                      className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </motion.div>
              </LanguageLink>
            </ScrollAnimation>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          className="text-center mt-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <LanguageLink
            to="/services"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
          >
            {t("services_section.cta_button")}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </LanguageLink>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
